import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Spacer,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { GroupUserRelation, LE2UsersType } from '@le2/common-types'
import { ExclamationCircleOutline, InformationCircleOutline, SearchOutline, TrashOutline } from 'heroicons-react'
import { Fragment, useEffect, useImperativeHandle, useState } from 'react'
import { useSnapshot } from 'valtio'

import useFetchUsers from '../hooks/useFetchUsers'
import { groupState } from '../utils'
import { FormRefI } from './GroupUpsertDialog'

interface UsersSelectionProps {
  formRef: React.RefObject<FormRefI>
}

export default function UsersSelection({ formRef }: UsersSelectionProps) {
  const { users: usersSnap, operation: operationSnap } = useSnapshot(groupState)
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')

  function clearUser(index: number) {
    const { users } = groupState

    users.splice(index, 1)

    groupState.users = [...users]
  }

  function clearAll() {
    groupState.users = []
  }

  useImperativeHandle(formRef, () => ({
    // Returns if the form is valid.
    submit() {
      return new Promise((resolve) => {
        if (usersSnap.length === 0 && operationSnap !== 'edit') {
          setErrorMessage('The group requires a minimum of one user')
          resolve(false)
        } else {
          resolve(true)
        }
      })
    }
  }))

  return (
    <Box>
      <Heading as="h5" size="sm" mb={3}>
        Add Users
      </Heading>
      <UserSelector value={usersSnap} onChange={(users) => (groupState.users = [...users])} />

      {errorMessage && (
        <Flex my={3}>
          <Icon w={6} h={6} color="red.500" as={ExclamationCircleOutline} />
          <Text ml={1} color="red.400">
            {errorMessage}
          </Text>
        </Flex>
      )}
      <Flex my={5}>
        <Heading alignSelf="center" as="h5" size="sm">
          Selected Users
        </Heading>
        <Spacer />
        {usersSnap.length !== 0 && (
          <Box>
            <Button
              onClick={clearAll}
              alignSelf="center"
              variant="outline"
              bgColor="gray.200"
              color="gray.500"
              size="sm"
            >
              Remove all
            </Button>
          </Box>
        )}
      </Flex>

      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Username</Th>
            <Th>Department</Th>
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {usersSnap.map((user, index) => (
            <Tr mb={5} key={index}>
              <UserInfo user={user} />

              <Td>
                <IconButton
                  color="gray.400"
                  size="xs"
                  variant="outline"
                  data-cy={`delete-user-${user.username}`}
                  aria-label={`delete-user-${index}`}
                  onClick={() => clearUser(index)}
                  icon={<Icon w={4} h={4} as={TrashOutline} />}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {usersSnap.length === 0 && (
        <Flex alignItems="center" px="220px" py="120px">
          <InformationCircleOutline color="gray" />{' '}
          <Text color="gray.500" ml={2}>
            No users selected
          </Text>
        </Flex>
      )}
    </Box>
  )
}

interface UserInfoProps {
  user: LE2UsersType
}

function UserInfo({ user }: UserInfoProps) {
  const { firstName, lastName, email, username, department } = user

  return (
    <Fragment>
      <Td>
        <Tooltip label={`name: ${firstName} ${lastName}`}>
          <Text minWidth="130px" maxWidth="130px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {firstName} {lastName}
          </Text>
        </Tooltip>
      </Td>
      <Td>
        <Tooltip label={`email: ${email}`}>
          <Text minWidth="120px" maxWidth="120px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {email}
          </Text>
        </Tooltip>
      </Td>
      <Td>
        <Tooltip label={`username: ${username}`}>
          <Text minWidth="90px" maxW="90px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {username}
          </Text>
        </Tooltip>
      </Td>

      <Td>
        {department ? (
          <Tooltip label={`deparment: ${department}`}>
            <Text>{department}</Text>
          </Tooltip>
        ) : (
          <Text>No department</Text>
        )}
      </Td>
    </Fragment>
  )
}

interface UserSelectorProps {
  onChange: (users: GroupUserRelation[]) => void
  value: Readonly<GroupUserRelation[]>
}

interface UserSelectorOptionType {
  user: LE2UsersType
  selected: boolean
}

function UserSelector({ onChange, value }: UserSelectorProps) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState<LE2UsersType[]>([])

  const fetch = useFetchUsers()

  const options: UserSelectorOptionType[] = users.map((user) => {
    return {
      user,
      selected: value.findIndex((option) => option.id === user.id) >= 0
    }
  })

  useEffect(() => {
    const init = async () => {
      const users = await fetch('')

      setUsers(users)
    }

    init()
  }, [fetch])

  async function handleSearch(search: string) {
    try {
      setIsLoading(true)
      const users = await fetch(search)

      setUsers(users)
    } catch (err) {
      if (err instanceof Error) {
        console.warn(err.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  function toggleCheck(user: LE2UsersType, isChecked: boolean) {
    const selected = [...value]

    if (isChecked) {
      selected.push({
        ...user,
        addedAt: new Date()
      })
    } else {
      const index = selected.findIndex((option) => user.id === option.id)

      if (index >= 0) {
        selected.splice(index, 1)
      }
    }

    onChange(selected)
  }

  function toggleSelectAll(isCheckedAll: boolean) {
    if (isCheckedAll) {
      onChange([])
    } else {
      const selectedIds: number[] = value.map(({ id }) => id)
      const newUsers = users.reduce((users, user) => {
        if (!selectedIds.includes(user.id)) {
          users.push({
            ...user,
            addedAt: new Date()
          })
        }

        return users
      }, [] as GroupUserRelation[])

      const newSelected = [...value, ...newUsers]

      onChange(newSelected)
    }
  }

  return (
    <Popover closeOnEsc={true} autoFocus={false} isOpen={isOpen} placement={'bottom-start'}>
      <PopoverTrigger>
        <InputGroup>
          <InputLeftElement pointerEvents="none" children={<SearchOutline color="gray.700" size={14} />} />
          <Input
            data-cy="add-users"
            placeholder="Search"
            onClick={(e) => {
              e.preventDefault()
              onOpen()
            }}
            onChange={(e) => handleSearch(e.currentTarget.value)}
          />
        </InputGroup>
      </PopoverTrigger>
      <Flex>
        <Box zIndex="popover">
          <PopoverContent w={650} maxH={560} overflow="auto" _focus={{ outline: 'none', boxShadow: 'none' }}>
            <PopoverBody>
              <UserOptionsTable
                options={options}
                isLoading={isLoading}
                onToggleCheck={toggleCheck}
                onToggleSelectAll={toggleSelectAll}
              />
            </PopoverBody>
            <PopoverFooter display="flex" alignItems="center" justifyContent="end" pb={4}>
              <Button size="sm" variant="outline" onClick={onClose}>
                Close
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </Box>
      </Flex>
    </Popover>
  )
}

interface UserOptionsTableProps {
  options: UserSelectorOptionType[]
  isLoading: boolean
  onToggleCheck: (user: LE2UsersType, isChecked: boolean) => void
  onToggleSelectAll: (isCheckedAll: boolean) => void
}

function UserOptionsTable({ options, isLoading, onToggleCheck, onToggleSelectAll }: UserOptionsTableProps) {
  const allChecked = options.filter((option) => option.selected).length === options.length

  function handleToggleSelectAll() {
    onToggleSelectAll(allChecked)
  }

  return (
    <>
      {options.length > 1 && (
        <Box px={4} py={2}>
          <Checkbox isChecked={allChecked} onChange={() => handleToggleSelectAll()}>
            Select all
          </Checkbox>
        </Box>
      )}
      <Table size="sm">
        {!isLoading && options.length === 0 && <TableCaption>No users results</TableCaption>}
        {isLoading && <TableCaption>Loading results</TableCaption>}
        <Thead bgColor="#F7F8FC">
          <Tr fontWeight={700} fontSize={12}>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Username</Th>
            <Th>Department</Th>
          </Tr>
        </Thead>
        <Tbody>
          {!isLoading && (
            <>
              {options.map((option, key) => (
                <UserOptionRow option={option} key={key} onToggleCheck={onToggleCheck} />
              ))}
            </>
          )}
        </Tbody>
      </Table>
    </>
  )
}

interface SelectOptionRowProps {
  option: UserSelectorOptionType
  onToggleCheck: (user: LE2UsersType, isChecked: boolean) => void
}

function UserOptionRow({ option, onToggleCheck }: SelectOptionRowProps) {
  const { firstName, lastName, email, username, department } = option.user

  return (
    <Tr>
      <Td data-cy="userSelectionColumn">
        <HStack>
          <Checkbox
            data-cy={`add-user-${username}`}
            isChecked={option.selected}
            onChange={(e) => onToggleCheck(option.user, e.currentTarget.checked)}
          />
          <Tooltip label={`${firstName} ${lastName}`}>
            <Text minWidth="130px" maxWidth="130px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
              {firstName} {lastName}
            </Text>
          </Tooltip>
        </HStack>
      </Td>
      <Td>
        <Tooltip label={email}>
          <Text minWidth="120px" maxWidth="120px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {email}
          </Text>
        </Tooltip>
      </Td>
      <Td>
        <Tooltip label={username}>
          <Text minWidth="90px" maxW="90px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {username}
          </Text>
        </Tooltip>
      </Td>
      <Td>
        {department ? (
          <Tooltip label={department}>
            <Text>{department}</Text>
          </Tooltip>
        ) : (
          <Text>No department</Text>
        )}
      </Td>
    </Tr>
  )
}
